import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FavoriteButton } from "./FavoriteButton";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function TextCard({ text: t, index }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const delay = Math.min(index * 90, 420);

  return (
    <li
      ref={ref}
      className="relative border transition-all duration-700 ease-out hover:border-[var(--rincon-secondary)]"
      style={{
        background: "var(--rincon-surface)",
        borderColor: "var(--rincon-border)",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(18px)",
        transitionDelay: `${delay}ms`,
      }}
      data-testid={`text-item-${t.id}`}
    >
      <div className="absolute top-4 right-4 z-10">
        <FavoriteButton
          text={t}
          className="p-2 rounded-full backdrop-blur-sm"
          style={{ background: "rgba(253,251,247,0.85)" }}
        />
      </div>
      <Link to={`/texto/${t.id}`} className="block group p-6 md:p-10">
        <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-start">
          <div className={t.image ? "md:col-span-8" : "md:col-span-12"}>
            <div
              className="text-xs uppercase tracking-[0.25em] mb-3"
              style={{ color: "var(--rincon-secondary)" }}
            >
              {t.main_category} · {t.subcategory}
              <span className="mx-2 opacity-40">/</span>
              <span style={{ color: "var(--rincon-text-muted)" }}>
                {formatDate(t.created_at)}
              </span>
            </div>
            <h2
              className="font-display text-2xl md:text-3xl lg:text-4xl leading-tight tracking-tight transition-colors"
              style={{ color: "var(--rincon-text)" }}
            >
              <span className="group-hover:text-[var(--rincon-primary)] transition-colors">
                {t.title}
              </span>
            </h2>
            {t.epigraph ? (
              <div
                className="mt-2 font-body italic text-base md:text-lg"
                style={{ color: "var(--rincon-secondary)" }}
              >
                «{t.epigraph}»
              </div>
            ) : null}
            <p
              className="font-body mt-4 max-w-2xl line-clamp-3"
              style={{ color: "var(--rincon-text-muted)", fontSize: "1.05rem" }}
            >
              {t.content.slice(0, 240)}
              {t.content.length > 240 ? "…" : ""}
            </p>
            {t.tags && t.tags.length > 0 ? (
              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1">
                {t.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs italic"
                    style={{ color: "var(--rincon-secondary)" }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          {t.image ? (
            <div className="md:col-span-4 order-first md:order-last">
              <div
                className="aspect-[4/3] bg-cover bg-center overflow-hidden"
                style={{
                  backgroundImage: `url(${t.image})`,
                  filter: "sepia(0.05)",
                }}
                aria-hidden="true"
              />
            </div>
          ) : null}
        </div>
      </Link>
    </li>
  );
}

const PAGE_SIZE = 12;

export default function TextList({ texts, emptyMessage = "Aún no hay textos publicados." }) {
  const [page, setPage] = useState(0);
  const [prevTexts, setPrevTexts] = useState(texts);

  // Patrón React: reiniciar la página al cambiar la lista (durante el render).
  if (texts !== prevTexts) {
    setPrevTexts(texts);
    setPage(0);
  }

  if (!texts || texts.length === 0) {
    return (
      <div
        className="py-24 text-center font-display italic text-2xl"
        style={{ color: "var(--rincon-text-muted)" }}
        data-testid="text-list-empty"
      >
        {emptyMessage}
      </div>
    );
  }

  const totalPages = Math.ceil(texts.length / PAGE_SIZE);
  const paginated = texts.length > PAGE_SIZE;
  const start = page * PAGE_SIZE;
  const visible = paginated ? texts.slice(start, start + PAGE_SIZE) : texts;

  const goTo = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="text-list">
      <ul className="space-y-6 md:space-y-8">
        {visible.map((t, i) => (
          <TextCard key={t.id} text={t} index={i} />
        ))}
      </ul>

      {paginated ? (
        <div
          className="mt-14 pt-8 border-t flex items-center justify-between"
          style={{ borderColor: "var(--rincon-border)" }}
          data-testid="text-list-pagination"
        >
          <button
            type="button"
            onClick={() => goTo(page - 1)}
            disabled={page === 0}
            data-testid="pagination-prev"
            className="inline-flex items-center gap-2 font-display text-lg ink-link disabled:opacity-30"
            style={{ color: "var(--rincon-primary)" }}
          >
            <ChevronLeft className="w-4 h-4" /> Anterior
          </button>
          <div
            className="text-xs uppercase tracking-[0.25em]"
            style={{ color: "var(--rincon-text-muted)" }}
            data-testid="pagination-status"
          >
            Página {page + 1} de {totalPages}
          </div>
          <button
            type="button"
            onClick={() => goTo(page + 1)}
            disabled={page >= totalPages - 1}
            data-testid="pagination-next"
            className="inline-flex items-center gap-2 font-display text-lg ink-link disabled:opacity-30"
            style={{ color: "var(--rincon-primary)" }}
          >
            Siguiente <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
